import type { ViewLoader, ViewBundle } from "../components/PageShell";
import type { ViewName } from "../view/useView";

/**
 * Every view's three files, as lazy loaders.
 *
 * import.meta.glob hands back a map of path -> () => Promise<contents> and
 * leaves each one in its own chunk, which is the point: a phone fetches the
 * mobile build of one page and nothing else. Importing the files outright
 * would put both views of all eight pages in the main bundle - /insights
 * alone ships 400KB of script per view.
 */
const files = {
  style: import.meta.glob("./*/*/style.css", { query: "?raw", import: "default" }),
  body: import.meta.glob("./*/*/body.html", { query: "?raw", import: "default" }),
  script: import.meta.glob("./*/*/script.js", { query: "?raw", import: "default" }),
} as Record<string, Record<string, () => Promise<string>>>;

function pick(kind: keyof typeof files, page: string, view: ViewName) {
  const path = `./${page}/${view}/${
    kind === "style" ? "style.css" : kind === "body" ? "body.html" : "script.js"
  }`;
  const loader = files[kind][path];
  if (!loader) {
    throw new Error(
      `No ${kind} for ${page}/${view} (looked for ${path}). ` +
        `Every page needs both a desktop/ and a mobile/ directory.`
    );
  }
  return loader;
}

/**
 * Cached per page: PageShell takes this record as an effect dependency, so
 * handing back a fresh object on every render would tear the view down and
 * rebuild it on every render too.
 */
const cache = new Map<string, Record<ViewName, ViewLoader>>();

export function viewsFor(page: string): Record<ViewName, ViewLoader> {
  const hit = cache.get(page);
  if (hit) return hit;

  const build = (view: ViewName): ViewLoader => async (): Promise<ViewBundle> => {
    const [styleText, bodyHtml, scriptText] = await Promise.all([
      pick("style", page, view)(),
      pick("body", page, view)(),
      pick("script", page, view)(),
    ]);
    return { styleText, bodyHtml, scriptText };
  };

  const record: Record<ViewName, ViewLoader> = {
    desktop: build("desktop"),
    mobile: build("mobile"),
  };
  cache.set(page, record);
  return record;
}
